import { supabase } from '../lib/supabase.js';
import MilestoneTemplateService from './MilestoneTemplateService.js';
import AcademicCalendarService from './AcademicCalendarService.js';
import TaskDateCalculationService from './TaskDateCalculationService.js';
import WeeklyCapacityService from './WeeklyCapacityService.js';
// TaskBatchingService removed - batching now handled at presentation layer
import { format } from 'date-fns';

/**
 * Ensure we have a valid authentication session before proceeding
 * This helps prevent RLS policy violations
 */
async function ensureAuthentication() {
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error) {
    console.error('[AdminMilestoneService] Auth session error:', error);
    throw new Error(`Authentication error: ${error.message}`);
  }
  
  if (!session?.user) {
    console.error('[AdminMilestoneService] No authenticated user found');
    throw new Error('No authenticated user. Please log in and try again.');
  }
  
  console.log('[AdminMilestoneService] Authentication verified:', {
    userId: session.user.id
  });
  
  return { session, userId: session.user.id };
}

class AdminMilestoneService {
  
  /**
   * Generate college-specific workflow key from college name
   * e.g., "University of Chicago" → "complete_university_of_chicago_app"
   */
  static getCollegeSpecificWorkflowKey(collegeName) {
    if (!collegeName) return 'complete_college_app';

    // Convert to lowercase, replace spaces with underscores, remove special chars
    const slug = collegeName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .trim()
      .replace(/\s+/g, '_'); // Replace spaces with underscores

    return `complete_${slug}_app`;
  }

  /**
   * Map milestone template priority to timeline_items constraint values
   * Fixes priority check constraint violations
   */
  static mapPriorityToTimelineItems(templatePriority) {
    const priorityMap = {
      // Map various template priority values to timeline_items allowed values
      'mandatory': 'critical',
      'critical': 'critical',
      'high': 'high',
      'medium': 'medium',
      'normal': 'medium',
      'low': 'low',
      // Default for any unmapped values
      null: 'medium',
      undefined: 'medium'
    };

    const mappedPriority = priorityMap[templatePriority] || 'medium';
    console.log(`[AdminMilestoneService] Priority mapping: "${templatePriority}" -> "${mappedPriority}"`);
    return mappedPriority;
  }

  /**
   * Clean up task titles for better readability
   * Transform "Submit application to specific college" + "Harvard University" 
   * Into "Submit application to Harvard"
   */
  static generateCleanTaskTitle(templateTitle, collegeName, applicationRound) {
    console.log(`[AdminMilestoneService] generateCleanTaskTitle called:`, {
      templateTitle,
      collegeName,
      applicationRound
    });
    
    let title = templateTitle;
    
    // Handle application submission tasks with round context
    if (title.toLowerCase().includes('submit application') || 
        title.toLowerCase().includes('application submission') || 
        title.toLowerCase().includes('submit common application')) {
      const roundLabel = applicationRound === 'early_decision' ? 'Early Decision' :
                        applicationRound === 'early_decision_2' ? 'Early Decision II' :
                        applicationRound === 'early_action' ? 'Early Action' :
                        'Regular Decision';
      const cleanTitle = `Submit ${roundLabel} Application to ${collegeName}`;
      console.log(`[AdminMilestoneService] Application submission task transformed:`, {
        original: templateTitle,
        clean: cleanTitle
      });
      return cleanTitle;
    }
    
    // More aggressive cleaning for simpler titles
    title = title.replace(/college-specific/gi, '');
    title = title.replace(/to specific college/gi, `to ${collegeName}`);
    title = title.replace(/for specific college/gi, `for ${collegeName}`);
    title = title.replace(/at specific college/gi, `at ${collegeName}`);
    title = title.replace(/from specific college/gi, `from ${collegeName}`);
    title = title.replace(/with specific college/gi, `with ${collegeName}`);
    title = title.replace(/for this college/gi, `for ${collegeName}`);
    title = title.replace(/to this college/gi, `to ${collegeName}`);
    title = title.replace(/at this college/gi, `at ${collegeName}`);
    
    // Simplify common verbose phrases
    title = title.replace(/Submit financial aid application/gi, 'Submit financial information');
    title = title.replace(/Complete College-Specific Essays/gi, `Complete essays for ${collegeName}`);
    title = title.replace(/Add College Essays to Tracking System/gi, `Add essays to tracking`);
    title = title.replace(/Submit supplements/gi, `Submit supplements to ${collegeName}`);
    title = title.replace(/Submit Common Application/gi, `Submit application`);
    
    // Clean up extra spaces and ensure college name is included properly
    title = title.replace(/\s+/g, ' ').trim();
    
    // Ensure college context without redundant " - College" format for cleaner look
    if (!title.toLowerCase().includes(collegeName.toLowerCase())) {
      // For specific task types, use different formats
      if (title.toLowerCase().includes('submit') && title.toLowerCase().includes('application')) {
        title = title.replace(/to$/, `to ${collegeName}`);
      } else if (!title.toLowerCase().match(/(fafsa|css profile|sat|act)/)) {
        // Only add college name if it's college-specific and doesn't already have it
        // Note: Removed "transcript" from exclusion so "Request Transcripts" gets college name
        title = `${title} to ${collegeName}`;
      }
    }
    
    console.log(`[AdminMilestoneService] Final clean title:`, {
      original: templateTitle,
      final: title
    });
    
    return title;
  }
  
  /**
   * Determine if a milestone template should be college-specific or global
   * Based on the template's title and purpose
   */
  static isCollegeSpecificTemplate(template) {
    const title = template.title.toLowerCase();
    const description = (template.description || '').toLowerCase();
    
    // College-specific indicators
    const collegeSpecificIndicators = [
      'submit college-specific supplements',
      'supplemental', 
      'school-specific',
      'visit college campuses',  // Each campus visit is per college
      'connect with alumni',     // Usually per college
      'research college programs', // Research each college's programs
      'for this college',        // New Tier 2 templates
      'college-specific',        // Various college-specific tasks
      'join college mailing',    // Per college engagement
      'complete value proposition', // Per college strategy
      'complete strategic research', // Per college research
      'populate college data',   // Per college data gathering
      'generate college essay ideas', // Per college essay brainstorming
      'verify and refine college essays', // Per college essay work
      'college essays to tracking', // Per college essay management
      'complete college-specific essays', // Obviously per college
      'this college'            // Generic indicator for college-specific tasks
    ];
    
    // Global indicators (these should only be done once)
    const globalIndicators = [
      'complete fafsa',         // Done once federally 
      'complete css profile',   // Done once, sent to multiple schools
      'register for sat/act',   // Test registration is once per test date
      'complete test prep',     // Test prep is general preparation
      'brainstorm essay topics', // General topic brainstorming (not college-specific)
      'complete basic information', // Profile setup is global
      'add academic information',   // Academic profile is global
      'document extracurriculars',  // Activity list is global
      'list awards and honors',     // Honors list is global  
      'create initial college list', // Initial research is global
      'identify career interests',   // Career exploration is global
      'attend college fairs',       // General college fairs (not college-specific)
      'gather tax documents'         // Financial documents are global
    ];
    
    // Check for college-specific patterns
    for (const indicator of collegeSpecificIndicators) {
      if (title.includes(indicator) || description.includes(indicator)) {
        return true;
      }
    }
    
    // Check for global patterns  
    for (const indicator of globalIndicators) {
      if (title.includes(indicator) || description.includes(indicator)) {
        return false;
      }
    }
    
    // Default logic based on category and phase
    if (template.category === 'essay') {
      // Most essay tasks are per-college except brainstorming
      return !title.includes('brainstorm') && !title.includes('topic');
    }
    
    if (template.category === 'application') {
      // Application tasks are mixed - some global, some per-college
      return title.includes('supplement') || title.includes('specific');
    }
    
    // Default: testing, financial_aid, profile, and research are mostly global
    return false;
  }



  /**
   * Alias for backward compatibility - generates global milestones
   */
  static async generateMilestonesForStudent(studentId) {
    console.log(`\n🔍 [METHOD ENTRY DEBUG] 🔍`);
    console.log(`[AdminMilestoneService] ===== generateMilestonesForStudent CALLED =====`);
    console.log(`[AdminMilestoneService] Student ID: ${studentId}`);
    console.log(`[AdminMilestoneService] 🚨 ADMIN INTERFACE ENTRY POINT FOR GENERAL MILESTONES`);
    
    return this.generateGlobalMilestonesForStudent(studentId);
  }

  /**
   * Generate immediate college-specific milestones (when college is added)
   * These are early strategic tasks that don't depend on deadlines
   */
  static async generateImmediateCollegeMilestones(studentId, collegeId, collegeName, options = {}) {
    // Simple wrapper for immediate college milestone generation
    // Timeline pressure and intensity filtering is handled inside generateCollegeMilestonesWithTiming
    return this.generateCollegeMilestonesWithTiming(studentId, collegeId, collegeName, 'immediate', options);
  }

  /**
   * Generate immediate college milestones for ALL colleges with cross-college batching
   * This is the new method that enables true batching across colleges
   */
  static async generateImmediateCollegeMilestonesWithBatching(studentId, colleges, options = {}) {
    try {
      console.log(`\n🔍 [METHOD ENTRY DEBUG] 🔍`);
      console.log(`[AdminMilestoneService] ===== generateImmediateCollegeMilestonesWithBatching CALLED =====`);
      console.log(`[AdminMilestoneService] Student ID: ${studentId}`);
      console.log(`[AdminMilestoneService] Number of colleges: ${colleges.length}`);
      console.log(`[AdminMilestoneService] Colleges:`, colleges.map(c => c.name || c.id));
      console.log(`[AdminMilestoneService] Options:`, options);
      console.log(`[AdminMilestoneService] 🚨 ADMIN INTERFACE ENTRY POINT FOR BATCHED COLLEGE MILESTONES`);
      
      await ensureAuthentication();
      console.log(`[AdminMilestoneService] Starting cross-college batching milestone generation for ${colleges.length} colleges`);
      
      // Step 1: Collect all milestone tasks from all colleges
      const allTasks = [];
      const results = { total: 0, errors: [], collegesProcessed: 0 };
      
      for (const college of colleges) {
        try {
          console.log(`[AdminMilestoneService] Collecting tasks for ${college.name}...`);

          // Get tasks without applying batching (collect only)
          // Only 'immediate' tasks - 'with_deadlines' are generated by triggers based on college requirements
          const collegeTasks = await this.collectCollegeTasks(studentId, college.id, college.name, 'immediate', options);

          // Add college context to each task
          collegeTasks.forEach(task => {
            task.source_college_id = college.id;
            task.source_college_name = college.name;
          });

          allTasks.push(...collegeTasks);
          results.collegesProcessed++;
          
        } catch (collegeError) {
          console.error(`Error collecting tasks for ${college.name}:`, collegeError);
          results.errors.push({
            college: college.name,
            error: collegeError.message
          });
        }
      }
      
      console.log(`[AdminMilestoneService] Collected ${allTasks.length} tasks from ${results.collegesProcessed} colleges`);
      
      if (allTasks.length === 0) {
        console.log(`[AdminMilestoneService] No tasks to process`);
        return { ...results, total: 0 };
      }
      
      // Step 2: Get student preferences for batching
      const studentPreferences = await this.getStudentTimelinePreferences(studentId);
      
      // Step 3: Apply academic calendar awareness first
      let processedTasks = allTasks;
      if (studentPreferences) {
        processedTasks = AcademicCalendarService.adjustTaskScheduling(processedTasks, studentPreferences);
        processedTasks = AcademicCalendarService.distributeWorkload(processedTasks, studentPreferences);
        console.log(`[AdminMilestoneService] Applied academic calendar adjustments to ${processedTasks.length} tasks`);
      }
      
      // Step 4: Skip batching - this is now handled at presentation layer
      // Batching is applied dynamically in UnifiedTimelineIntegrated.jsx

      // Step 5: Apply capacity management (skip in crisis mode - we want tight packing)
      const timelinePressure = studentPreferences?.disclosure_pace || 'steady';
      if (timelinePressure !== 'crisis' && studentPreferences) {
        console.log(`[AdminMilestoneService] Applying capacity management to ${processedTasks.length} tasks (${timelinePressure} mode)`);
        processedTasks = WeeklyCapacityService.redistributeOverloadedTasks(processedTasks, studentPreferences);
      } else if (timelinePressure === 'crisis') {
        console.log(`[AdminMilestoneService] Skipping capacity redistribution in crisis mode - maintaining tight timeline`);
      }
      
      console.log(`[AdminMilestoneService] Final task count after cross-college batching and capacity management: ${processedTasks.length}`);

      // 🔍 DEBUG: Check workflow_key before database insert
      console.log(`\n🔍 [PRE-INSERT DEBUG] First 3 tasks before database insert:`);
      processedTasks.slice(0, 3).forEach((task, idx) => {
        console.log(`  Task ${idx + 1}: "${task.title}"`);
        console.log(`    workflow_key: "${task.workflow_key}"`);
        console.log(`    sub_workflow_key: "${task.sub_workflow_key}"`);
        console.log(`    college_name: "${task.college_name}"`);
      });

      // Step 6: Insert all processed tasks to database
      if (processedTasks.length > 0) {
        const { error: insertError } = await supabase
          .from('timeline_items')
          .insert(processedTasks);
          
        if (insertError) {
          console.error('[AdminMilestoneService] Error inserting batched milestones:', insertError);
          throw new Error(`Failed to insert batched milestones: ${insertError.message}`);
        }
        
        results.total = processedTasks.length;
        console.log(`[AdminMilestoneService] Successfully inserted ${processedTasks.length} batched milestones`);
      }
      
      return results;
      
    } catch (error) {
      console.error('[AdminMilestoneService] Error in cross-college batching:', error);
      throw new Error(`Cross-college batching failed: ${error.message}`);
    }
  }

  /**
   * Collect milestone tasks for a college without applying batching
   * This is a helper method for cross-college batching
   */
  static async collectCollegeTasks(studentId, collegeId, collegeName, timing, options = {}) {
    // This is similar to generateCollegeMilestonesWithTiming but only collects tasks without inserting
    const { parentInfo } = await ensureAuthentication();
    console.log(`[AdminMilestoneService] Collecting ${timing} tasks for ${collegeName}`);
    
    // Get timeline pressure and baseline date
    console.log(`[AdminMilestoneService] 🔍 COLLECTING TASKS - FETCHING STUDENT PROFILE FOR: ${studentId}`);
    const { data: profile } = await supabase
      .from('student_academic_profiles')
      .select('timeline_pressure, timeline_baseline_date, graduation_year, name, created_at')
      .eq('user_id', studentId)
      .single();
      
    console.log(`[AdminMilestoneService] 📊 COLLECTING TASKS - PROFILE QUERY RESULT:`, {
      found: !!profile,
      timeline_pressure: profile?.timeline_pressure || 'null',
      timeline_baseline_date: profile?.timeline_baseline_date || 'null',
      graduation_year: profile?.graduation_year || 'null',
      name: profile?.name || 'null'
    });

    let timelinePressure = 'steady';
    if (profile?.timeline_pressure) {
      timelinePressure = profile.timeline_pressure;
    }
    console.log(`[AdminMilestoneService] Timeline pressure for task collection: ${timelinePressure}`);

    // Get college data
    // SYNC WITH: Trigger functions use global_colleges for deadlines
    // Deadlines are on global_colleges, NOT college_journey_colleges
    const { data: college, error: collegeError } = await supabase
      .from('college_journey_colleges')
      .select(`
        id, name, category, global_college_id,
        global_colleges (
          id, name,
          ed_deadline, ed2_deadline, ea_deadline, rd_deadline,
          created_at, updated_at
        )
      `)
      .eq('id', collegeId)
      .single();

    if (collegeError || !college) {
      throw new Error(`College data not found: ${collegeError?.message}`);
    }

    const allTasks = [];
    const categories = ['application', 'essay', 'testing', 'financial_aid', 'profile', 'research'];
    
    for (const category of categories) {
      console.log(`[AdminMilestoneService] Collecting ${category} tasks for ${collegeName}`);
      
      // Get templates for this category and timing
      let query = supabase
        .from('milestone_templates')
        .select('*')
        .eq('category', category)
        .eq('generation_timing', timing)
        .eq('is_college_specific', true)
        .eq('is_active', true);

      const { data: templates, error: templateError } = await query;

      if (templateError) {
        console.error(`Error fetching ${category} templates:`, templateError);
        continue;
      }

      if (!templates || templates.length === 0) {
        console.log(`No ${category} templates found for ${timing} timing`);
        continue;
      }

      // 🔍 DEBUG: Log fetched templates to verify workflow_key exists
      console.log(`\n🔍 [DEBUG] Fetched ${templates.length} templates for category=${category}, timing=${timing}`);
      templates.forEach(t => {
        console.log(`  - "${t.title}": workflow_key="${t.workflow_key}", cognitive_intensity="${t.cognitive_intensity}", mvv_time_required="${t.mvv_time_required}"`);
      });
      
      // Apply timeline pressure filtering
      console.log(`[AdminMilestoneService] Filtering ${templates.length} ${category} templates by timeline pressure: ${timelinePressure}`);
      const filteredTemplates = this.filterTemplatesByIntensity(templates, timelinePressure);
      console.log(`[AdminMilestoneService] After filtering: ${filteredTemplates.length} ${category} templates`);
      
      // Convert templates to milestone tasks (but don't insert yet)
      for (const template of filteredTemplates) {
        try {
          console.log(`\n🎯 [COLLEGE MILESTONE DEBUG] 🎯`);
          console.log(`[AdminMilestoneService] ===== PROCESSING COLLEGE MILESTONE =====`);
          console.log(`[AdminMilestoneService] Template: "${template.title}" (Category: ${template.category})`);
          console.log(`[AdminMilestoneService] College: ${collegeName}`);
          console.log(`[AdminMilestoneService] Timeline Pressure: ${timelinePressure}`);
          console.log(`[AdminMilestoneService] Timing: ${timing}`);
          console.log(`[AdminMilestoneService] 🚀 CALLING TaskDateCalculationService.calculateDueDate()...`);
          
          // Create proper student object from profile data
          const student = {
            id: studentId,
            name: profile?.name || 'Student',
            timeline_baseline_date: profile?.timeline_baseline_date,
            created_at: profile?.created_at,
            graduation_year: profile?.graduation_year,
            timeline_pressure: timelinePressure
          };
          
          console.log(`[AdminMilestoneService] 👤 STUDENT OBJECT CREATED:`, {
            id: student.id,
            name: student.name,
            timeline_baseline_date: student.timeline_baseline_date || 'null',
            timeline_pressure: student.timeline_pressure
          });
          
          // Fixed: Pass proper student object and college object
          const dueDate = await TaskDateCalculationService.calculateDueDate(
            template,
            student,
            college
          );
          
          console.log(`[AdminMilestoneService] ✅ RESULT: ${dueDate.toISOString().split('T')[0]}`);
          console.log(`[AdminMilestoneService] ===== END COLLEGE MILESTONE =====\n`);

          const cleanTitle = this.generateCleanTaskTitle(
            template.title,
            collegeName,
            college.application_round || 'regular_decision'
          );

          // Determine task subtype for batching
          const taskSubtype = this.getTaskSubtype(template);

          const milestoneTask = {
            user_id: studentId,
            source_type: 'milestone_template',
            source_id: `template_${template.id}_college_${collegeId}`,
            title: cleanTitle,
            description: template.description,
            scheduled_date: dueDate ? format(new Date(dueDate), 'yyyy-MM-dd') : null,
            due_date: dueDate ? format(new Date(dueDate), 'yyyy-MM-dd') : null,
            estimated_duration: template.estimated_duration,
            category: template.category,
            priority: this.mapPriorityToTimelineItems(template.priority),
            epic_id: template.epic_id || null,
            college_id: collegeId,
            college_name: collegeName,
            workflow_phase: 'ready',
            status: 'planned',
            milestone_id: template.id,
            intensity_level: template.intensity_level,
            tags: template.tags || [],

            // Workflow hierarchy fields (for workflow view)
            // For ALL college-specific tasks, use college-specific workflow key
            // Keep sub_workflow_key from template (for consistent grouping across colleges)
            workflow_key: this.getCollegeSpecificWorkflowKey(collegeName),
            sub_workflow_key: template.sub_workflow_key || null,
            hierarchy_level: template.hierarchy_level || 3,
            display_order: template.display_order || 0,
            workflow_metadata: template.workflow_metadata || {},

            // Prerequisite/unlock fields
            is_foundation_task: template.is_foundation_template || false,
            unlock_description: template.unlock_description || null,

            // MVV (Minimum Viable Version) fields
            min_viable_version: template.min_viable_version || null,
            mvv_time_required: template.mvv_time_required || null,
            mvv_risk: template.mvv_risk || null,
            optimal_version: template.optimal_version || null,
            optimal_time_required: template.optimal_time_required || null,
            incompletion_cost: template.incompletion_cost || null,

            // AI/Autogeneration fields
            can_autogenerate: template.can_autogenerate || null,
            autogen_quality: template.autogen_quality || null,

            // Action/UI fields
            action_label: template.action_label || null,
            action_route: template.action_route || null,
            module_name: template.module_name,
            module_link: this.getCollegeSpecificLink(template, collegeId, collegeName),

            metadata: {
              template_id: template.id,
              batchable: this.isTaskBatchable(template),
              subtype: taskSubtype
            }
          };

          // 🔍 DEBUG: Log task object before adding to verify workflow_key is present
          console.log(`\n🔍 [DEBUG] Task object for "${milestoneTask.title}":`);
          console.log(`  workflow_key: "${milestoneTask.workflow_key}"`);
          console.log(`  sub_workflow_key: "${milestoneTask.sub_workflow_key}"`);
          console.log(`  cognitive_intensity: "${template.cognitive_intensity}"`);
          console.log(`  mvv_time_required: "${milestoneTask.mvv_time_required}"`);
          console.log(`  autogen_quality: "${milestoneTask.autogen_quality}"`);

          allTasks.push(milestoneTask);
          
        } catch (taskError) {
          console.error(`Error processing template ${template.title}:`, taskError);
        }
      }
    }
    
    console.log(`[AdminMilestoneService] Collected ${allTasks.length} tasks for ${collegeName}`);
    return allTasks;
  }

  /**
   * Determine if a task template should be batchable
   */
  static isTaskBatchable(template) {
    // Profile tasks with value propositions are batchable
    if (template.category === 'profile' &&
        (template.title.toLowerCase().includes('value proposition') ||
         template.title.toLowerCase().includes('why this college'))) {
      return true;
    }

    // Research tasks are batchable
    if (template.category === 'research') {
      return true;
    }

    // Application setup tasks are batchable
    if (template.title.toLowerCase().includes('portal') ||
        template.title.toLowerCase().includes('application account')) {
      return true;
    }

    return false;
  }

  /**
   * Get the task subtype for batching purposes
   */
  static getTaskSubtype(template) {
    const title = template.title.toLowerCase();

    // Value proposition tasks
    if (title.includes('value proposition')) {
      return 'value_proposition';
    }
    if (title.includes('why this college')) {
      return 'why_this_college';
    }

    // Research tasks
    if (template.category === 'research') {
      if (title.includes('strategic')) {
        return 'strategic_research';
      }
      if (title.includes('culture')) {
        return 'campus_culture_research';
      }
      if (title.includes('program')) {
        return 'program_research';
      }
      return 'college_research';
    }

    // Application setup
    if (title.includes('portal')) {
      return 'create_portal_account';
    }
    if (title.includes('application') && title.includes('account')) {
      return 'start_application';
    }

    // Financial aid
    if (title.includes('css')) {
      return 'css_profile_school';
    }
    if (title.includes('fafsa')) {
      return 'fafsa_school_code';
    }

    return template.id || 'general';
  }

  /**
   * Generate college-specific milestones as admin
   * These are tasks that need to be done for each individual college
   * Supports timing-based filtering for backward compatibility (defaults to deadline-based)
   * Now generates both immediate AND with_deadlines templates to ensure comprehensive task creation
   * Now includes timeline pressure by default
   */
  static async generateGlobalMilestonesForStudent(studentId) {
    try {
      await ensureAuthentication();
      console.log(`[AdminMilestoneService] Global milestone generation with timeline pressure for student ${studentId}`);
      
      // Calculate timeline pressure (this becomes standard behavior)
      const pressureInfo = await this.calculateTimelinePressure(studentId);
      console.log(`Timeline pressure: ${pressureInfo.timelinePressure} (${pressureInfo.daysUntilDeadline} days)`);
      
      // Delete existing milestones (but preserve trigger-generated tasks)
      // DO NOT delete tasks generated by database triggers (recommendations, tier milestones, essay workflow)
      const { error: deleteError } = await supabase
        .from('timeline_items')
        .delete()
        .eq('user_id', studentId)
        .in('source_type', ['milestone_template', 'milestone_subtask'])
        .is('metadata->generated_by_trigger', null); // Only delete non-trigger tasks

      if (deleteError) {
        console.error('[AdminMilestoneService] Error deleting existing milestones:', deleteError);
      }

      // Get templates and filter by intensity (now standard)
      const { data: allTemplates } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('generation_timing', 'with_global')
        .eq('is_active', true)
        .eq('is_college_specific', false);

      console.log(`[DEBUG] Global milestone generation: Starting with ${allTemplates?.length || 0} templates before intensity filtering`);
      const filteredTemplates = this.filterTemplatesByIntensity(allTemplates || [], pressureInfo.timelinePressure);
      console.log(`[AdminMilestoneService] Global: Filtered ${allTemplates?.length || 0} templates to ${filteredTemplates.length} based on ${pressureInfo.timelinePressure} pressure`);

      // Load student data for anchor calculations
      const { data: studentProfile } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', studentId)
        .single();

      const student = {
        id: studentId,
        timeline_pressure: pressureInfo.timelinePressure,
        timeline_baseline_date: studentProfile?.timeline_baseline_date,
        created_at: studentProfile?.created_at,
        first_name: studentProfile?.name?.split(' ')[0] || 'Student',
        last_name: studentProfile?.name?.split(' ')[1] || '',
        graduation_year: studentProfile?.graduation_year
      };

      // Load student preferences for academic calendar awareness
      const studentPreferences = await this.getStudentTimelinePreferences(studentId);
      console.log(`[AdminMilestoneService] Student preferences:`, studentPreferences);

      // Generate milestones using anchor-based calculation
      let milestones = await Promise.all(filteredTemplates.map(async template => {
        // Use the anchor-based calculation system
        const dueDate = await this.calculateDueDateWithAnchorSystem(template, student);
        return {
          user_id: studentId,
          title: template.title,
          description: template.description || '',
          category: template.category,
          epic_id: template.epic_id || null, // Make epic_id nullable to avoid FK constraint errors
          priority: this.mapPriorityToTimelineItems(template.priority),
          due_date: dueDate,
          scheduled_date: dueDate,
          workflow_phase: 'ready',
          status: 'planned',
          completion_rule: template.completion_rule,
          source_type: 'milestone_template',
          source_id: `template_${template.id}`,

          // Time estimation fields
          estimated_duration: template.estimated_duration || 60,

          // Module/resource fields
          module_name: template.module_name,
          module_link: template.module_link || template.action_route,
          resource_urls: template.resource_urls || [],

          // Workflow hierarchy fields (for workflow view)
          workflow_key: template.workflow_key || null,
          sub_workflow_key: template.sub_workflow_key || null,
          hierarchy_level: template.hierarchy_level || 3,
          display_order: template.display_order || 0,
          workflow_metadata: template.workflow_metadata || {},

          // Prerequisite/unlock fields
          is_foundation_task: template.is_foundation_template || false,
          unlock_description: template.unlock_description || null,

          // MVV (Minimum Viable Version) fields
          min_viable_version: template.min_viable_version || null,
          mvv_time_required: template.mvv_time_required || null,
          mvv_risk: template.mvv_risk || null,
          optimal_version: template.optimal_version || null,
          optimal_time_required: template.optimal_time_required || null,
          incompletion_cost: template.incompletion_cost || null,

          // AI/Autogeneration fields
          can_autogenerate: template.can_autogenerate || null,
          autogen_quality: template.autogen_quality || null,

          // Action/UI fields
          action_label: template.action_label || null,
          action_route: template.action_route || null,

          // Task metadata fields
          intensity_level: template.intensity_level || null,
          importance_level: template.importance_level || null,
          cognitive_intensity: template.cognitive_intensity || null,
          timing_sensitivity: template.timing_sensitivity || null,

          metadata: {
            template_id: template.id,
            phase: template.phase,
            timeline_pressure: pressureInfo.timelinePressure,
            generated_at: new Date().toISOString(),
            batchable: this.isTaskBatchable(template),
            subtype: this.getTaskSubtype(template)
          }
        };
      }));

      // Apply academic calendar awareness and workload distribution
      if (studentPreferences) {
        milestones = AcademicCalendarService.adjustTaskScheduling(milestones, studentPreferences);
        milestones = AcademicCalendarService.distributeWorkload(milestones, studentPreferences);
        console.log(`[AdminMilestoneService] Applied academic calendar adjustments`);
        
        // Skip batching here - batching is now handled at presentation layer
        // See UnifiedTimelineIntegrated.jsx for dynamic batching implementation

        // Apply capacity management (skip in crisis mode - we want tight packing)
        const timelinePressure = studentPreferences.disclosure_pace || 'steady';
        if (timelinePressure !== 'crisis') {
          console.log(`[AdminMilestoneService] Applying capacity management to ${milestones.length} milestones (${timelinePressure} mode)`);
          milestones = WeeklyCapacityService.redistributeOverloadedTasks(milestones, studentPreferences);
        } else {
          console.log(`[AdminMilestoneService] Skipping capacity redistribution in crisis mode - maintaining tight timeline`);
        }
        
        console.log(`[AdminMilestoneService] Final milestone count after batching and capacity management: ${milestones.length}`);
      }

      if (milestones.length > 0) {
        const { error } = await supabase
          .from('timeline_items')
          .upsert(milestones, { onConflict: 'user_id,source_id' });

        if (error) {
          console.error('[AdminMilestoneService] Error inserting milestones:', error);
          throw error;
        }
      }

      return {
        total: milestones.length,
        timelinePressure: pressureInfo.timelinePressure,
        daysUntilDeadline: pressureInfo.daysUntilDeadline,
        filteredFromTotal: allTemplates?.length || 0,
        type: 'global_with_pressure'
      };
    } catch (error) {
      console.error('Error in global milestone generation:', error);
      throw error;
    }
  }

  /**
   * REPLACE: generateCollegeMilestones  
   * Now includes timeline pressure by default
   */
  static async generateCollegeMilestones(studentId, collegeId, collegeName, options = {}) {
    try {
      console.log(`\n🔍 [METHOD ENTRY DEBUG] 🔍`);
      console.log(`[AdminMilestoneService] ===== generateCollegeMilestones CALLED =====`);
      console.log(`[AdminMilestoneService] Student ID: ${studentId}`);
      console.log(`[AdminMilestoneService] College ID: ${collegeId}`);
      console.log(`[AdminMilestoneService] College Name: ${collegeName}`);
      console.log(`[AdminMilestoneService] Options:`, options);
      console.log(`[AdminMilestoneService] 🚨 ADMIN INTERFACE ENTRY POINT FOR COLLEGE MILESTONES`);
      
      await ensureAuthentication();
      console.log(`[AdminMilestoneService] College milestone generation with timeline pressure for ${collegeName}`);
      
      // Get student profile data including graduation year
      const { data: profile } = await supabase
        .from('student_academic_profiles')
        .select('timeline_pressure, graduation_year, timeline_baseline_date, name')
        .eq('user_id', studentId)
        .single();

      const timelinePressure = profile?.timeline_pressure || 'steady';
      const studentGraduationYear = profile?.graduation_year || (new Date().getFullYear() + 1);
      
      // Your existing college data logic
      // SYNC WITH: Trigger functions use global_colleges for deadlines
      // Deadlines are on global_colleges, NOT college_journey_colleges
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          global_college_id,
          application_round,
          essay_requirements,
          supplemental_essays_status,
          global_colleges!global_college_id(
            ed_deadline, ed2_deadline, ea_deadline, rd_deadline,
            finaid_deadline, css_deadline,
            app_platforms, num_essays, essay_prompts,
            interview_policy
          )
        `)
        .eq('id', collegeId)
        .single();

      if (collegeError || !college) {
        throw new Error(`College data error: ${collegeError?.message}`);
      }

      let totalGenerated = 0;
      const allErrors = [];

      // Generate both immediate and deadline-based (your existing approach)
      for (const timing of ['immediate', 'with_deadlines']) {
        try {
          const { data: allTemplates } = await supabase
            .from('milestone_templates')
            .select('*')
            .eq('generation_timing', timing)
            .eq('is_active', true)
            .eq('is_college_specific', true);

          if (!allTemplates || allTemplates.length === 0) continue;

          // Filter by timeline pressure (now standard)
          const filteredTemplates = this.filterTemplatesByIntensity(allTemplates, timelinePressure);
          
          // Your existing duplicate checking
          const { data: existing } = await supabase
            .from('timeline_items')
            .select('source_id')
            .eq('user_id', studentId)
            .in('source_id', filteredTemplates.map(t => `template_${t.id}_college_${collegeId}`));
          
          const existingSourceIds = new Set((existing || []).map(e => e.source_id));

          // Filter templates that should be processed
          // Note: We already filtered by is_college_specific=true in the query
          const deadline = this.getRelevantDeadline(college, options.applicationRound);

          const eligibleTemplates = filteredTemplates.filter(template => {
            if (template.tier_level === 'tier3') return false;
            const sourceId = `template_${template.id}_college_${collegeId}`;
            if (existingSourceIds.has(sourceId)) return false;

            // NEW: Check if template is appropriate for deadline
            if (!this.isTemplateAppropriateForDeadline(template, deadline)) {
              console.log(`[Phase Filter] Skipping "${template.title}" - too close to deadline (${deadline})`);
              return false;
            }

            return true;
          });

          // Process templates with async due date calculation
          const milestones = [];
          for (const template of eligibleTemplates) {
            // NEW: Enhanced due date calculation using anchor system
            const dueDate = await this.calculateDueDateWithAnchorSystem(template, {
              id: studentId,
              timeline_pressure: timelinePressure,
              timeline_baseline_date: profile?.timeline_baseline_date,
              graduation_year: studentGraduationYear,
              first_name: profile?.name?.split(' ')[0] || 'Student',
              last_name: profile?.name?.split(' ')[1] || ''
            }, college);

            milestones.push({
              user_id: studentId,
              title: this.generateCleanTaskTitle(
                  template.title,
                  collegeName,
                  options.applicationRound || college.application_round
                ),
                description: template.description || '',
                category: template.category,
                epic_id: template.epic_id || null, // Make epic_id nullable to avoid FK constraint errors
                college_id: collegeId,
                college_name: collegeName,
                priority: this.mapPriorityToTimelineItems(template.priority),
                due_date: dueDate,
                scheduled_date: dueDate,
                workflow_phase: 'ready',
                status: 'planned',
                completion_rule: template.completion_rule,
                source_type: 'milestone_template',
                source_id: `template_${template.id}_college_${collegeId}`,

                // Time estimation fields
                estimated_duration: template.estimated_duration || 60,

                // Module/resource fields
                module_name: template.module_name,
                module_link: this.getCollegeSpecificLink(template, collegeId, collegeName),
                resource_urls: template.resource_urls || [],

                // Workflow hierarchy fields (for workflow view)
                // For ALL college-specific tasks, use college-specific workflow key
                // Keep sub_workflow_key from template (for consistent grouping across colleges)
                workflow_key: this.getCollegeSpecificWorkflowKey(collegeName),
                sub_workflow_key: template.sub_workflow_key || null,
                hierarchy_level: template.hierarchy_level || 3,
                display_order: template.display_order || 0,
                workflow_metadata: template.workflow_metadata || {},

                // Prerequisite/unlock fields
                is_foundation_task: template.is_foundation_template || false,
                unlock_description: template.unlock_description || null,

                // MVV (Minimum Viable Version) fields
                min_viable_version: template.min_viable_version || null,
                mvv_time_required: template.mvv_time_required || null,
                mvv_risk: template.mvv_risk || null,
                optimal_version: template.optimal_version || null,
                optimal_time_required: template.optimal_time_required || null,
                incompletion_cost: template.incompletion_cost || null,

                // AI/Autogeneration fields
                can_autogenerate: template.can_autogenerate || null,
                autogen_quality: template.autogen_quality || null,

                // Action/UI fields
                action_label: template.action_label || null,
                action_route: template.action_route || null,

                // Task metadata fields
                intensity_level: template.intensity_level || null,
                importance_level: template.importance_level || null,
                cognitive_intensity: template.cognitive_intensity || null,
                timing_sensitivity: template.timing_sensitivity || null,

                metadata: {
                  template_id: template.id,
                  task_type: 'college_specific',
                  timeline_pressure: timelinePressure,
                  generated_at: new Date().toISOString(),
                  batchable: this.isTaskBatchable(template),
                  subtype: this.getTaskSubtype(template)
                }
            });
          }

          if (milestones.length > 0) {
            const { error } = await supabase
              .from('timeline_items')
              .upsert(milestones, { onConflict: 'user_id,source_id' });

            if (error) {
              allErrors.push({ timing, error: error.message });
            } else {
              totalGenerated += milestones.length;
            }
          }
        } catch (error) {
          allErrors.push({ timing, error: error.message });
        }
      }

      // Essay slots (generate once after both timing iterations)
      let essaySlots = 0;
      try {
        const EssaySlotService = (await import('./EssaySlotService')).default;
        const slots = await EssaySlotService.generateSlotsForCollege(studentId, collegeId);
        essaySlots = slots?.length || 0;
      } catch (essayError) {
        allErrors.push({ timing: 'essay_slots', error: essayError.message });
      }

      return {
        total: totalGenerated,
        errors: allErrors,
        essaySlots: essaySlots,
        timelinePressure: timelinePressure
      };
    } catch (error) {
      console.error('Error in college milestone generation:', error);
      throw error;
    }
  }


  /**
   * Internal method for generating college milestones with timing filtering
   */
  static async generateCollegeMilestonesWithTiming(studentId, collegeId, collegeName, timing, options = {}) {
    try {
      console.log(`\n🔍 [METHOD ENTRY DEBUG] 🔍`);
      console.log(`[AdminMilestoneService] ===== generateCollegeMilestonesWithTiming CALLED =====`);
      console.log(`[AdminMilestoneService] Student ID: ${studentId}`);
      console.log(`[AdminMilestoneService] College ID: ${collegeId}`);
      console.log(`[AdminMilestoneService] College Name: ${collegeName}`);
      console.log(`[AdminMilestoneService] Timing: ${timing}`);
      console.log(`[AdminMilestoneService] Options:`, options);
      console.log(`[AdminMilestoneService] 🚨 THIS IS A POTENTIAL SOURCE OF AUGUST 26 DATES`);
      
      // Ensure we have proper authentication before proceeding
      const { parentInfo } = await ensureAuthentication();
      console.log(`[AdminMilestoneService] Starting ${timing} college milestone generation for ${collegeName} (student: ${studentId})`);
      
      // NEW: Get student's timeline pressure AND timeline_baseline_date
      console.log(`[AdminMilestoneService] 🔍 FETCHING STUDENT PROFILE FOR: ${studentId}`);
      const { data: profile, error: profileError } = await supabase
        .from('student_academic_profiles')
        .select('timeline_pressure, timeline_baseline_date, graduation_year, name, created_at')
        .eq('user_id', studentId)
        .single();
        
      console.log(`[AdminMilestoneService] 📊 PROFILE QUERY RESULT:`, {
        found: !!profile,
        error: profileError?.message || 'none',
        timeline_pressure: profile?.timeline_pressure || 'null',
        timeline_baseline_date: profile?.timeline_baseline_date || 'null',
        graduation_year: profile?.graduation_year || 'null',
        name: profile?.name || 'null'
      });
  
      const timelinePressure = profile?.timeline_pressure || 'steady';
      const studentGraduationYear = profile?.graduation_year || (new Date().getFullYear() + 1);
      console.log(`[DEBUG] TIMELINE PRESSURE CHECK: Student ${studentId} profile:`, profile);
      console.log(`[DEBUG] TIMELINE PRESSURE RESULT: Using timeline pressure '${timelinePressure}' for ${timing} templates`);
      
      // Additional check - let's also verify what we expect vs what we got
      if (timelinePressure !== 'crisis') {
        console.log(`[DEBUG] ⚠️  EXPECTED CRISIS BUT GOT: '${timelinePressure}' - this might explain why all templates are included`);
      } else {
        console.log(`[DEBUG] ✅ Timeline pressure is crisis as expected - filtering should be active`);
      }
  
      // Get college data by joining with global_colleges for deadline information
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          global_college_id,
          application_round,
          essay_requirements,
          supplemental_essays_status,
          global_colleges!global_college_id(
            ed_deadline, ed2_deadline, ea_deadline, rd_deadline,
            finaid_deadline, css_deadline,
            app_platforms, num_essays, essay_prompts,
            interview_policy
          )
        `)
        .eq('id', collegeId)
        .single();
  
      console.log('College query result:', { college, collegeError });
  
      if (collegeError) {
        throw new Error(`Database error for college ${collegeId}: ${collegeError.message}`);
      }
  
      if (!college) {
        throw new Error(`No college found with journey ID ${collegeId}`);
      }
  
      if (!college.global_college_id) {
        throw new Error(`College "${collegeName}" is missing global_college_id link`);
      }
  
      if (!college.global_colleges) {
        throw new Error(`Global college data not found for "${collegeName}" (global_id: ${college.global_college_id})`);
      }
  
      // Import validation utility
      const { CollegeDataCompleteness } = await import('../utils/collegeDataCompleteness');
      const validation = CollegeDataCompleteness.validateCollege({
        id: collegeId,
        name: collegeName,
        ...college.global_colleges,
        essay_requirements: college.essay_requirements,
        supplemental_essays_status: college.supplemental_essays_status
      });
  
      console.log('College validation:', validation);
  
      // Check if we should proceed with incomplete data
      // Note: Immediate generation (early tasks) is more forgiving than deadline-based generation
      if (!validation.readyForTasks && !options.forceGenerate && timing !== 'immediate') {
        console.log('Validation failed, not generating milestones');
        return {
          total: 0,
          errors: [`College "${collegeName}" missing required data: ${validation.missing.join(', ')}`],
          validation,
          canForceGenerate: true
        };
      }
      
      // For immediate generation, we can proceed with warnings
      if (timing === 'immediate' && !validation.isComplete) {
        console.log(`[AdminMilestoneService] Proceeding with immediate generation despite incomplete data for ${collegeName}`);
      }
  
      // For immediate generation, don't restrict by phase since these are strategic tasks
      // For "Generate Tasks" functionality, also don't restrict by phase to get all college-specific tasks
      // Only restrict by phase for automated/scheduled generation (not implemented yet)
      let phase = null;
      if (timing !== 'immediate' && !options.forceGenerate && !options.autoGenerated) {
        const currentMonth = new Date().getMonth();
        phase = 'preparation';
        if (currentMonth >= 8 && currentMonth <= 11) phase = 'application';
        else if (currentMonth >= 0 && currentMonth <= 4) phase = 'decision';
      }
  
      console.log('Current month:', new Date().getMonth(), 'Phase for filtering:', phase || 'all phases (no phase restriction)');
  
      const results = { total: 0, errors: [] };
  
      // Note: Essay slots are only generated for deadline-based college tasks,
      // not immediate college tasks. This prevents RLS errors and follows proper workflow.
  
      // Generate college-specific milestones from ALL categories (not just application and essay)
      const categories = ['application', 'essay', 'testing', 'financial_aid', 'profile', 'research'];
      
      for (const category of categories) {
        console.log(`[AdminMilestoneService] Processing category: ${category} for ${collegeName}`);
        try {
          // Skip essay template generation if essay slots already exist AND have timeline items
          if (category === 'essay') {
            console.log(`[DEBUG] ${category}/${timing}: Checking existing essay slots for ${collegeName}...`);
            const { data: existingEssaySlots, error: slotsError } = await supabase
              .from('essay_slots')
              .select('id, timeline_item_id')
              .eq('user_id', studentId)
              .eq('college_id', collegeId);
            
            if (slotsError) {
              console.log(`[DEBUG] ${category}/${timing}: Error checking essay slots: ${slotsError.message}`);
            } else {
              console.log(`[DEBUG] ${category}/${timing}: Found ${existingEssaySlots?.length || 0} total essay slots`);
            }
            
            // Only skip if we have essay slots AND they have timeline items
            const slotsWithTimelineItems = existingEssaySlots?.filter(slot => slot.timeline_item_id) || [];
            if (!slotsError && slotsWithTimelineItems.length > 0) {
              console.log(`[DEBUG] ${category}/${timing}: ⏭️  SKIPPING ENTIRE CATEGORY - ${slotsWithTimelineItems.length} essay slots with timeline items already exist for ${collegeName}`);
              continue;
            }
            console.log(`[DEBUG] ${category}/${timing}: ✅ Proceeding with essay template generation - no essay slots with timeline items found for ${collegeName}`);
          }
          
          let query = supabase
            .from('milestone_templates')
            .select('*')
            .eq('category', category)
            .eq('is_active', true)
            .eq('generation_timing', timing)
            .eq('is_college_specific', true);
          
          // Only filter by phase for non-immediate generation
          if (phase) {
            query = query.eq('phase', phase);
          }
          
          const { data: allTemplates, error: templatesError } = await query;
  
          console.log(`[DEBUG] Found ${allTemplates?.length || 0} ${timing} templates for ${category}${phase ? `/${phase}` : ' (all phases)'}`);
          
          if (templatesError) {
            console.error(`[AdminMilestoneService] Error fetching templates for ${category}:`, templatesError);
            continue;
          }
          
          // Filter templates based on college application round
          const typeFilteredTemplates = this.filterTemplatesByCollegeType(allTemplates, college.application_round);
          console.log(`[DEBUG] After college type filtering: ${typeFilteredTemplates.length} applicable templates for ${college.name} (${college.application_round})`);
  
          // Filter templates based on college status (accepted/selected) 
          const applicableTemplates = this.filterTemplatesByCollegeStatus(typeFilteredTemplates, college);
          console.log(`[DEBUG] After college status filtering: ${applicableTemplates.length} applicable templates for ${college.name}`);
  
          if (!applicableTemplates || applicableTemplates.length === 0) {
            console.log(`[AdminMilestoneService] No applicable templates found for ${category} after filtering, skipping`);
            continue;
          }
          
          // Show what intensity levels we have in the templates
          const intensityBreakdown = {};
          applicableTemplates.forEach(t => {
            const intensity = t.intensity_level || 'standard';
            intensityBreakdown[intensity] = (intensityBreakdown[intensity] || 0) + 1;
          });
          console.log(`[DEBUG] ${category}/${timing} intensity breakdown:`, intensityBreakdown);
          console.log(`[DEBUG] ${category}/${timing} sample templates:`, applicableTemplates.slice(0, 3).map(t => ({ title: t.title, intensity: t.intensity_level || 'standard' })));
          
          // Filter templates by intensity based on timeline pressure
          console.log(`[DEBUG] ${category}/${timing}: Starting with ${applicableTemplates.length} templates before intensity filtering`);
          const templates = this.filterTemplatesByIntensity(applicableTemplates, timelinePressure);
          console.log(`[AdminMilestoneService] ${category}/${timing}: Filtered ${applicableTemplates.length} to ${templates.length} templates based on ${timelinePressure} pressure`);
          
          if (templates.length === 0) {
            console.log(`[AdminMilestoneService] No templates remain after intensity filtering for ${category}, skipping`);
            continue;
          }
          
          console.log(`[AdminMilestoneService] Final templates for ${category}:`, templates.map(t => t.title));
  
          // Check existing with broader query to catch all duplicates
          const { data: existing, error: existingError } = await supabase
            .from('timeline_items')
            .select('source_id, user_id, source_type')
            .eq('user_id', studentId)
            .in('source_id', templates.map(t => `template_${t.id}_college_${collegeId}`));
          
          console.log(`Checking for existing with user_id: ${studentId}, source_ids:`, templates.map(t => `template_${t.id}_college_${collegeId}`));
  
          if (existingError) {
            console.error('Error checking existing milestones:', existingError);
          }
  
          const existingSourceIds = new Set((existing || []).map(e => e.source_id));
          console.log(`Found ${existing?.length || 0} existing milestones for ${category}/${phase}:`, existingSourceIds);
  
          // Filter eligible templates with detailed debugging
          console.log(`[DEBUG] ${category}/${timing}: Starting eligibility filtering for ${templates.length} templates`);
          const eligibleTemplates2 = [];
          const skippedTemplates = [];
          
          templates.forEach(template => {
            const sourceId = `template_${template.id}_college_${collegeId}`;
            let skipReason = null;
            
            // Note: We already filtered by is_college_specific=true in the query, 
            // so no need to double-check with isCollegeSpecificTemplate()
            
            // Check tier level
            if (template.tier_level === 'tier3') {
              skipReason = 'T3 conditional template - requires special setup';
            }
            // Check if already exists
            else if (existingSourceIds.has(sourceId)) {
              skipReason = 'Milestone already exists';
            }
            
            if (skipReason) {
              skippedTemplates.push({ title: template.title, reason: skipReason, sourceId });
            } else {
              eligibleTemplates2.push(template);
            }
          });
          
          console.log(`[DEBUG] ${category}/${timing}: ELIGIBLE ${eligibleTemplates2.length} templates:`);
          eligibleTemplates2.forEach(t => console.log(`  ✅ ELIGIBLE: \"${t.title}\"`));
          
          if (skippedTemplates.length > 0) {
            console.log(`[DEBUG] ${category}/${timing}: SKIPPED ${skippedTemplates.length} templates:`);
            skippedTemplates.forEach(s => console.log(`  ❌ SKIPPED: \"${s.title}\" - ${s.reason}`));
          }

          // Process templates with async due date calculation
          let milestones = [];
          for (const template of eligibleTemplates2) {
            // Add validation warnings to task description if data incomplete
            let taskDescription = template.description || '';
            if (!validation.isComplete && validation.warnings.length > 0) {
              const relevantWarnings = validation.warnings.filter(warning => 
                (template.title.toLowerCase().includes('essay') && warning.includes('essay')) ||
                (template.title.toLowerCase().includes('deadline') && warning.includes('deadline')) ||
                (template.title.toLowerCase().includes('financial') && warning.includes('financial'))
              );
              if (relevantWarnings.length > 0) {
                taskDescription += `\n\n⚠️ Note: ${relevantWarnings[0]}`;
              }
            }

            // NEW: Enhanced due date calculation using anchor system
            const dueDate = await this.calculateDueDateWithAnchorSystem(template, {
              id: studentId,
              timeline_pressure: timelinePressure,
              timeline_baseline_date: profile?.timeline_baseline_date,
              graduation_year: studentGraduationYear,
              first_name: profile?.name?.split(' ')[0] || 'Student',
              last_name: profile?.name?.split(' ')[1] || ''
            }, college);

            milestones.push({
              user_id: studentId,
              title: this.generateCleanTaskTitle(
                  template.title,
                  collegeName,
                  options.applicationRound || college.application_round
                ),
                description: taskDescription,
                category: category,
                epic_id: template.epic_id || null, // Make epic_id nullable to avoid FK constraint errors
                college_id: collegeId,
                college_name: collegeName,
                priority: this.mapPriorityToTimelineItems(template.priority),
                due_date: dueDate,
                scheduled_date: dueDate,
                workflow_phase: 'ready',
                status: 'planned',
                completion_rule: template.completion_rule,
                source_type: 'milestone_template',
                source_id: `template_${template.id}_college_${collegeId}`,

                // Time estimation fields
                estimated_duration: template.estimated_duration || 60,

                // Module/resource fields
                module_name: template.module_name,
                module_link: this.getCollegeSpecificLink(template, collegeId, collegeName),
                resource_urls: template.resource_urls || [],

                // Workflow hierarchy fields (for workflow view)
                // For ALL college-specific tasks, use college-specific workflow key
                // Keep sub_workflow_key from template (for consistent grouping across colleges)
                workflow_key: this.getCollegeSpecificWorkflowKey(collegeName),
                sub_workflow_key: template.sub_workflow_key || null,
                hierarchy_level: template.hierarchy_level || 3,
                display_order: template.display_order || 0,
                workflow_metadata: template.workflow_metadata || {},

                // Prerequisite/unlock fields
                is_foundation_task: template.is_foundation_template || false,
                unlock_description: template.unlock_description || null,

                // MVV (Minimum Viable Version) fields
                min_viable_version: template.min_viable_version || null,
                mvv_time_required: template.mvv_time_required || null,
                mvv_risk: template.mvv_risk || null,
                optimal_version: template.optimal_version || null,
                optimal_time_required: template.optimal_time_required || null,
                incompletion_cost: template.incompletion_cost || null,

                // AI/Autogeneration fields
                can_autogenerate: template.can_autogenerate || null,
                autogen_quality: template.autogen_quality || null,

                // Action/UI fields
                action_label: template.action_label || null,
                action_route: template.action_route || null,

                // Task metadata fields
                intensity_level: template.intensity_level || null,
                importance_level: template.importance_level || null,
                cognitive_intensity: template.cognitive_intensity || null,
                timing_sensitivity: template.timing_sensitivity || null,

                metadata: {
                  template_id: template.id,
                  phase: phase,
                  offset_type: template.offset_type,
                  deadline_field: template.deadline_field,
                  task_type: 'college_specific',
                  milestone_scope: 'college',
                  data_complete: validation.isComplete,
                  generated_with_warnings: !validation.isComplete,
                  batchable: this.isTaskBatchable(template),
                  subtype: this.getTaskSubtype(template),
                  force_generated: options.forceGenerate || false,
                  template_dependencies: template.template_dependencies,
                  timeline_pressure: timelinePressure, // NEW: Track timeline pressure
                  generated_at: new Date().toISOString()
                }
            });
          }

          // Apply academic calendar awareness to college-specific milestones
          const studentPreferences = await this.getStudentTimelinePreferences(studentId);
          if (studentPreferences && milestones.length > 0) {
            milestones = AcademicCalendarService.adjustTaskScheduling(milestones, studentPreferences);
            milestones = AcademicCalendarService.distributeWorkload(milestones, studentPreferences);
            console.log(`[AdminMilestoneService] Applied academic calendar adjustments to ${milestones.length} college tasks`);
            
            // Skip batching here - batching is now handled at presentation layer
            // See UnifiedTimelineIntegrated.jsx for dynamic batching implementation

            // Apply capacity management (skip in crisis mode - we want tight packing)
            const timelinePressure = studentPreferences.disclosure_pace || 'steady';
            if (timelinePressure !== 'crisis') {
              console.log(`[AdminMilestoneService] Applying capacity management to ${milestones.length} ${category} milestones (${timelinePressure} mode)`);
              milestones = WeeklyCapacityService.redistributeOverloadedTasks(milestones, studentPreferences);
            } else {
              console.log(`[AdminMilestoneService] Skipping capacity redistribution in crisis mode for ${category} milestones - maintaining tight timeline`);
            }
            
            console.log(`[AdminMilestoneService] Final ${category} milestone count after batching and capacity management: ${milestones.length}`);
          }
  
          if (milestones.length > 0) {
            console.log(`Attempting to insert ${milestones.length} milestones for ${category}`);
            
            // Use upsert to handle any remaining duplicates gracefully
            const { error } = await supabase
              .from('timeline_items')
              .upsert(milestones, { 
                onConflict: 'user_id,source_id',
                ignoreDuplicates: false
              });
  
            if (error) {
              console.error(`Insert error for ${category}:`, error);
              console.error(`[AdminMilestoneService] College RLS Policy violation details:`, {
                errorCode: error.code,
                errorMessage: error.message,
                studentId,
                collegeId,
                collegeName,
                category,
                milestoneCount: milestones.length,
              });
              throw error;
            }
            results.total += milestones.length;
            console.log(`Successfully inserted ${milestones.length} milestones for ${category}`);
          }
        } catch (error) {
          console.error(`Error generating ${category} milestones for ${collegeName}:`, error);
          results.errors.push({ category, college: collegeName, error: error.message });
        }
      }
  
      return results;
    } catch (error) {
      console.error('Error generating college milestones:', error);
      throw error;
    }
  }

  /**
   * Delete and regenerate all milestones for a student
   * Uses database function to bypass RLS
   */
  static async regenerateMilestones(studentId) {
    
    try {
      // Use the database function that runs with SECURITY DEFINER
      const { data, error } = await supabase
        .rpc('admin_generate_milestones_for_student', {
          p_student_id: studentId
        });

      if (error) throw error;

      // Check if the function returned an error
      if (data && !data.success) {
        throw new Error(data.error || 'Failed to generate milestones');
      }

      return {
        total: data?.generated_count || 0,
        phase: data?.phase || 'unknown',
        errors: data?.errors || [],
        message: data?.message || 'Milestones generated'
      };
    } catch (error) {
      console.error('Error regenerating milestones:', error);
      throw error;
    }
  }
  
  /**
   * Preview what would be generated for a student
   */
  static async previewGeneration(studentId) {
    
    try {
      const { data, error } = await supabase
        .rpc('preview_milestone_generation', {
          p_student_id: studentId
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error previewing generation:', error);
      throw error;
    }
  }

  /**
   * Get the appropriate link for college-specific tasks
   * Enhanced to support URL patterns with dynamic substitution
   */
  static getCollegeSpecificLink(template, collegeId, collegeName = '') {
    // If template has a module_link with patterns, process it
    if (template.module_link && template.module_link.includes('{{')) {
      return this.processLinkPattern(template.module_link, collegeId, collegeName);
    }

    // Check if this template should link to college strategy page
    const collegeStrategyTemplates = [
      'value_proposition',
      'strategic_research',
      'why_this_college',
      'fit_statement',
      'populate_college_data',
      'college_research'
    ];

    const templateSubtype = AdminMilestoneService.getTaskSubtype(template);

    if (collegeStrategyTemplates.includes(templateSubtype) && collegeId) {
      return `/college/colleges/${collegeId}`;
    }

    // Otherwise use the template's default link
    return template.module_link || template.action_route || null;
  }

  /**
   * Process URL patterns with dynamic substitution
   * Supports patterns like: /college/colleges/{{college_id}}
   */
  static processLinkPattern(pattern, collegeId, collegeName = '') {
    if (!pattern) return null;

    let processedLink = pattern;

    // Replace common patterns
    processedLink = processedLink.replace(/\{\{college_id\}\}/g, collegeId || '');
    processedLink = processedLink.replace(/\{\{college_name\}\}/g, encodeURIComponent(collegeName || ''));

    // Clean up any remaining empty patterns or double slashes
    processedLink = processedLink.replace(/\/+/g, '/'); // Remove double slashes
    processedLink = processedLink.replace(/\/$/, ''); // Remove trailing slash

    return processedLink;
  }

  /**
   * Get the relevant deadline for a college based on application round
   */
  static getRelevantDeadline(college, applicationRound) {
    const round = applicationRound || college.application_round || 'regular';
    const gc = college.global_colleges;

    if (!gc) {
      console.warn('[Phase Filter] No global_colleges data available');
      return null;
    }

    switch(round) {
      case 'early_decision':
      case 'early_decision_1':
        return gc.ed_deadline;
      case 'early_decision_2':
        return gc.ed2_deadline;
      case 'early_action':
        return gc.ea_deadline;
      case 'regular':
      case 'regular_decision':
      default:
        return gc.rd_deadline;
    }
  }

  /**
   * Determine if template is appropriate for current date relative to deadline
   * Prevents generating research tasks when deadline is too close
   */
  static isTemplateAppropriateForDeadline(template, deadline, currentDate = new Date()) {
    if (!deadline) {
      // If no deadline, allow generation (shouldn't happen but be permissive)
      return true;
    }

    // Calculate days until deadline
    const deadlineDate = new Date(deadline);
    const daysUntilDeadline = Math.ceil((deadlineDate - currentDate) / (1000 * 60 * 60 * 24));

    // If deadline has passed, don't generate new tasks
    if (daysUntilDeadline < 0) {
      return false;
    }

    const title = (template.title || '').toLowerCase();
    const category = template.category;

    // Research/discovery tasks need significant lead time
    if (category === 'College Discovery' || category === 'research') {
      // Campus visits need at least 3 months
      if (title.includes('visit') && title.includes('campus')) {
        return daysUntilDeadline > 90;
      }
      // Other research tasks need at least 2 months
      return daysUntilDeadline > 60;
    }

    // Profile/foundation tasks need at least 45 days
    if (category === 'Profile & Foundation') {
      return daysUntilDeadline > 45;
    }

    // Essay development needs at least 30 days
    if (category === 'Essay Development') {
      return daysUntilDeadline > 30;
    }

    // Application execution tasks are appropriate until deadline
    if (category === 'Application Execution') {
      return daysUntilDeadline > 0;
    }

    // Financial aid tasks need at least 14 days
    if (category === 'Financial Planning') {
      return daysUntilDeadline > 14;
    }

    // Default: allow if more than 14 days before deadline
    return daysUntilDeadline > 14;
  }

  /**
   * Determine if a task template is suitable for batching
   */
  static isTaskBatchable(template) {
    // Don't batch critical deadline-specific tasks
    if (template.priority === 'critical' || template.priority === 'mandatory') {
      return false;
    }

    // Don't batch essays or long-form writing
    if (template.category === 'essay' && template.title?.toLowerCase().includes('essay')) {
      return false;
    }

    // Tasks that are good for batching
    const batchableKeywords = [
      'research', 'populate', 'verify', 'update', 'review',
      'value proposition', 'portal', 'account', 'submit scores',
      'transcript', 'css profile', 'fafsa'
    ];

    const title = template.title?.toLowerCase() || '';
    return batchableKeywords.some(keyword => title.includes(keyword));
  }

  /**
   * Get task subtype for batching categorization
   */
  static getTaskSubtype(template) {
    const title = template.title?.toLowerCase() || '';

    // Map common task types to subtypes
    if (title.includes('value proposition')) return 'value_proposition';
    if (title.includes('college research') || title.includes('strategic research')) return 'college_research';
    if (title.includes('populate') && title.includes('data')) return 'populate_college_data';
    if (title.includes('verify') && title.includes('deadline')) return 'verify_deadlines';
    if (title.includes('portal') || title.includes('account')) return 'create_portal_account';
    if (title.includes('transcript')) return 'request_transcript';
    if (title.includes('css profile')) return 'css_profile_school';
    if (title.includes('fafsa')) return 'fafsa_school_code';
    if (title.includes('submit') && title.includes('scores')) return 'send_scores';
    if (title.includes('campus') && title.includes('culture')) return 'campus_culture_research';
    if (title.includes('program') && title.includes('research')) return 'program_research';

    // Default to the template ID or category
    return `template_${template.id}`;
  }

  /**
   * Calculate due date based on template configuration
   * Updated to use signed offsets (positive = after, negative = before)
   */
  static calculateDueDate(template) {
    const offsetDays = template.default_due_offset || 0; // Now signed
    const offsetType = template.offset_type || 'from_creation';
    const baseDate = new Date();

    switch (offsetType) {
      case 'after_start': {
        // Anchor to academic year start (September 1st)
        const academicYearStart = new Date(baseDate.getFullYear(), 8, 1);
        if (baseDate < academicYearStart) {
          academicYearStart.setFullYear(academicYearStart.getFullYear() - 1);
        }
        const afterStartDate = new Date(academicYearStart);
        afterStartDate.setDate(afterStartDate.getDate() + offsetDays); // Simply add signed offset
        return afterStartDate.toISOString().split('T')[0];
      }

      case 'before_decision': {
        // Typically May 1st
        const decisionDate = new Date(baseDate.getFullYear() + 1, 4, 1);
        decisionDate.setDate(decisionDate.getDate() + offsetDays); // Add signed offset (negative will subtract)
        return decisionDate.toISOString().split('T')[0];
      }

      default: {
        // From creation date
        const fromCreationDate = new Date();
        fromCreationDate.setDate(fromCreationDate.getDate() + offsetDays); // Simply add signed offset
        return fromCreationDate.toISOString().split('T')[0];
      }
    }
  }

  /**
   * Calculate due date with college-specific deadlines and application round timing
   * Now includes timeline pressure by default and uses signed offsets
   */
  static calculateDueDateWithDeadlines(template, collegeData, options = {}) {
    const offsetType = template.offset_type || 'from_creation';
    const signedOffset = template.default_due_offset || 0; // Now signed
    const applicationRound = options.applicationRound || collegeData?.application_round || 'regular_decision';
    const timelinePressure = options.timelinePressure; // Now expected

    if (offsetType === 'before_deadline' && template.deadline_field) {
      let targetDeadline = null;

      if (collegeData && collegeData[template.deadline_field]) {
        targetDeadline = collegeData[template.deadline_field];
      } else {
        targetDeadline = this.getApplicationRoundDeadline(applicationRound);
      }

      if (targetDeadline) {
        // Apply timeline pressure compression (now standard)
        if (timelinePressure) {
          return this.calculateCompressedDueDate(
            { ...template, default_due_offset: signedOffset },
            timelinePressure,
            new Date(targetDeadline)
          );
        } else {
          // Fallback to original logic if timeline pressure not available
          const deadlineDate = new Date(targetDeadline);
          deadlineDate.setDate(deadlineDate.getDate() + signedOffset); // Simply add signed offset
          return deadlineDate.toISOString().split('T')[0];
        }
      }
    }

    // Fallback
    return this.calculateDueDate(template);
  }

  /**
   * Get standard application deadline based on application round
   */
  static getApplicationRoundDeadline(applicationRound) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-based
    
    // Determine academic year - if we're past June, it's the next academic year
    const academicYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
    
    switch (applicationRound) {
      case 'early_decision':
      case 'early_action':
        return `${academicYear - 1}-11-01`; // November 1st of senior year
        
      case 'regular_decision':
        return `${academicYear}-01-01`; // January 1st of senior year
        
      case 'rolling':
        // For rolling admissions, use a target 6 months from now
        const rollingDate = new Date();
        rollingDate.setMonth(rollingDate.getMonth() + 6);
        return rollingDate.toISOString().split('T')[0];
        
      default:
        return `${academicYear}-01-01`; // Default to Regular Decision
    }
  }

  /**
   * NEW HELPER METHODS (add these)
   */
  static async calculateTimelinePressure(studentId) {
    try {
      const { data: profile } = await supabase
        .from('student_academic_profiles')
        .select('graduation_year, timeline_pressure, timeline_baseline_date')
        .eq('user_id', studentId)
        .single();

      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select(`
          application_round,
          global_colleges!global_college_id(
            ed_deadline, ed2_deadline, ea_deadline, rd_deadline
          )
        `)
        .eq('user_id', studentId);

      let earliestDeadline = null;
      const currentDate = new Date();
      
      for (const college of colleges || []) {
        const deadlines = [
          college.global_colleges?.ed_deadline,
          college.global_colleges?.ed2_deadline, 
          college.global_colleges?.ea_deadline,
          college.global_colleges?.rd_deadline
        ].filter(Boolean).map(d => new Date(d));
        
        for (const deadline of deadlines) {
          if (deadline > currentDate && (!earliestDeadline || deadline < earliestDeadline)) {
            earliestDeadline = deadline;
          }
        }
      }

      if (!earliestDeadline) {
        const academicYear = profile?.graduation_year || new Date().getFullYear() + 1;
        earliestDeadline = new Date(`${academicYear}-01-01`);
      }

      const daysUntilDeadline = Math.ceil((earliestDeadline - currentDate) / (1000 * 60 * 60 * 24));
      
      let timelinePressure;
      if (daysUntilDeadline < 90) timelinePressure = 'crisis';
      else if (daysUntilDeadline < 180) timelinePressure = 'intensive'; 
      else if (daysUntilDeadline < 365) timelinePressure = 'steady';
      else timelinePressure = 'comprehensive';

      // Update student profile
      await supabase
        .from('student_academic_profiles')
        .update({
          timeline_pressure: timelinePressure,
          timeline_baseline_date: new Date().toISOString().split('T')[0],
          earliest_application_deadline: earliestDeadline.toISOString().split('T')[0]
        })
        .eq('user_id', studentId);

      return { timelinePressure, daysUntilDeadline, earliestDeadline: earliestDeadline.toISOString().split('T')[0] };
    } catch (error) {
      console.error('Error calculating timeline pressure:', error);
      // Graceful fallback
      return { timelinePressure: 'steady', daysUntilDeadline: 365, earliestDeadline: null };
    }
  }

  /**
   * Filter templates based on college application round visibility settings
   */
  static filterTemplatesByCollegeType(templates, applicationRound) {
    if (!templates || !Array.isArray(templates) || templates.length === 0) {
      return templates || [];
    }
    
    console.log(`[DEBUG] filterTemplatesByCollegeType: Filtering ${templates.length} templates for application round: ${applicationRound}`);
    
    const filtered = templates.filter(template => {
      // Default behavior: if no visibility flags are set, show for all college types
      const hasAnyVisibilityFlag = (
        template.show_for_ed_schools !== null ||
        template.show_for_ea_schools !== null ||
        template.show_for_rd_schools !== null ||
        template.show_for_waitlisted_schools !== null ||
        template.show_for_accepted_schools !== null ||
        template.show_for_selected_school !== null
      );
      
      if (!hasAnyVisibilityFlag) {
        // No visibility restrictions = show for all college types
        return true;
      }
      
      // Check specific application round visibility
      switch (applicationRound) {
        case 'early_decision':
        case 'early_decision_2':
          return template.show_for_ed_schools === true;
        case 'early_action':
          return template.show_for_ea_schools === true;
        case 'regular_decision':
          return template.show_for_rd_schools === true;
        case 'waitlist':
          return template.show_for_waitlisted_schools === true;
        default:
          // Unknown round - default to RD visibility
          return template.show_for_rd_schools !== false;
      }
    });
    
    const skipped = templates.length - filtered.length;
    if (skipped > 0) {
      console.log(`[DEBUG] filterTemplatesByCollegeType: Filtered out ${skipped} templates not applicable to ${applicationRound} colleges`);
    }
    
    return filtered;
  }

  /**
   * Filter templates based on college status (accepted/selected) for post-application phases
   * @param {Array} templates - Array of milestone templates
   * @param {Object} collegeData - College data with admission_decision and is_attending fields
   * @returns {Array} Filtered templates
   */
  static filterTemplatesByCollegeStatus(templates, collegeData) {
    if (!templates || !Array.isArray(templates) || templates.length === 0) {
      return templates || [];
    }
    
    console.log(`[DEBUG] filterTemplatesByCollegeStatus: Filtering ${templates.length} templates for college status`);
    
    const filtered = templates.filter(template => {
      // Check if template has any status-based visibility restrictions
      const hasStatusRestrictions = (
        template.show_for_accepted_schools !== null ||
        template.show_for_selected_school !== null
      );

      if (!hasStatusRestrictions) {
        // No status restrictions = always show (including planning stage)
        return true;
      }

      // Template HAS status restrictions - check if they match the college's status
      const isAccepted = collegeData.admission_decision === 'accepted';
      const isAttending = collegeData.is_attending === true;

      // If template requires accepted status
      if (template.show_for_accepted_schools === true) {
        if (!isAccepted) {
          console.log(`[DEBUG] Template "${template.title}": SKIPPED (requires accepted, college is ${collegeData.admission_decision || 'planning'})`);
          return false;
        }
      }

      // If template requires selected/attending status
      if (template.show_for_selected_school === true) {
        if (!isAttending) {
          console.log(`[DEBUG] Template "${template.title}": SKIPPED (requires attending, college is_attending=${collegeData.is_attending})`);
          return false;
        }
      }

      // If template explicitly excludes accepted schools
      if (template.show_for_accepted_schools === false && isAccepted) {
        console.log(`[DEBUG] Template "${template.title}": SKIPPED (excludes accepted schools)`);
        return false;
      }

      // If template explicitly excludes selected schools
      if (template.show_for_selected_school === false && isAttending) {
        console.log(`[DEBUG] Template "${template.title}": SKIPPED (excludes attending schools)`);
        return false;
      }

      // Template has restrictions but they don't exclude this college
      console.log(`[DEBUG] Template "${template.title}": INCLUDED (status restrictions don't exclude this college)`);
      return true;
    });
    
    const skipped = templates.length - filtered.length;
    if (skipped > 0) {
      console.log(`[DEBUG] filterTemplatesByCollegeStatus: Filtered out ${skipped} templates based on college status`);
    } 
    
    return filtered;
  }

  static filterTemplatesByIntensity(templates, timelinePressure) {
    // If templates array is empty or null, return as-is
    if (!templates || !Array.isArray(templates) || templates.length === 0) {
      console.log(`[DEBUG] filterTemplatesByIntensity: No templates to filter`);
      return templates || [];
    }
    
    const intensityLevels = {
      'crisis': ['crisis'],                       // Crisis: Only critical tasks
      'intensive': ['crisis', 'standard'],        // Intensive: Include essential tasks, skip comprehensive
      'steady': ['crisis', 'standard', 'comprehensive'],  // Steady: Include all tasks
      'comprehensive': ['crisis', 'standard', 'comprehensive']  // Comprehensive: Include all tasks
    };
    
    const allowedIntensities = intensityLevels[timelinePressure] || ['crisis', 'standard'];
    
    console.log(`[DEBUG] filterTemplatesByIntensity: Timeline pressure '${timelinePressure}' allows intensities: [${allowedIntensities.join(', ')}]`);
    
    // IMPORTANT: Check if any templates have intensity_level field
    const templatesWithIntensity = templates.filter(t => t.hasOwnProperty('intensity_level')).length;
    if (templatesWithIntensity === 0) {
      console.log(`[DEBUG] ⚠️  CRITICAL: No templates have intensity_level field! This explains why all templates are being generated.`);
      console.log(`[DEBUG] filterTemplatesByIntensity: All ${templates.length} templates will default to 'standard' intensity`);
      
      // NOTE: Emergency filtering disabled - keeping all tasks regardless of crisis mode
      // For crisis timeline pressure, we should still filter based on template characteristics
      // if (timelinePressure === 'crisis') {
      //   console.log(`[DEBUG] Crisis mode: Applying emergency filtering based on template titles`);
      //   return this.applyEmergencyFiltering(templates);
      // }
    }
    
    const kept = [];
    const skipped = [];
    
    templates.forEach(template => {
      const templateIntensity = template.intensity_level || 'standard';
      const shouldKeep = allowedIntensities.includes(templateIntensity);
      
      if (shouldKeep) {
        kept.push(template);
      } else {
        skipped.push({ title: template.title, intensity: templateIntensity, reason: `Intensity '${templateIntensity}' not allowed for '${timelinePressure}' timeline pressure` });
      }
    });
    
    console.log(`[DEBUG] filterTemplatesByIntensity: KEPT ${kept.length} templates:`);
    kept.forEach(t => console.log(`  ✅ KEPT: "${t.title}" (intensity: ${t.intensity_level || 'standard'})`));
    
    if (skipped.length > 0) {
      console.log(`[DEBUG] filterTemplatesByIntensity: SKIPPED ${skipped.length} templates:`);
      skipped.forEach(s => console.log(`  ❌ SKIPPED: "${s.title}" (intensity: ${s.intensity}) - ${s.reason}`));
    } else {
      console.log(`[DEBUG] filterTemplatesByIntensity: No templates skipped - this indicates the issue!`);
    }
    
    return kept;
  }

  /**
   * Emergency filtering when intensity_level fields are missing
   * Used when database doesn't have intensity_level values set
   */
  static applyEmergencyFiltering(templates) {
    console.log(`[DEBUG] applyEmergencyFiltering: Filtering ${templates.length} templates by title patterns for crisis mode`);
    
    // Essential tasks that should remain in crisis mode
    const essentialPatterns = [
      /submit.*application/i,
      /complete.*fafsa/i,
      /register.*(sat|act)/i,
      /transcript.*request/i,
      /application.*deadline/i,
      /css.*profile/i,
      /financial.*aid.*deadline/i
    ];
    
    // Non-essential tasks that should be skipped in crisis mode
    const nonEssentialPatterns = [
      /research.*program/i,
      /visit.*campus/i,
      /connect.*alumni/i,
      /join.*mailing/i,
      /brainstorm.*essay.*topic/i,
      /strategic.*research/i,
      /explore.*career/i,
      /attend.*college.*fair/i
    ];
    
    const kept = [];
    const skipped = [];
    
    templates.forEach(template => {
      const title = template.title || '';
      
      // Check if it's explicitly non-essential
      const isNonEssential = nonEssentialPatterns.some(pattern => pattern.test(title));
      if (isNonEssential) {
        skipped.push({ title, reason: 'Non-essential task for crisis timeline' });
        return;
      }
      
      // Keep essential tasks and standard tasks (default to keep unless explicitly non-essential)
      kept.push(template);
    });
    
    console.log(`[DEBUG] applyEmergencyFiltering: KEPT ${kept.length} templates, SKIPPED ${skipped.length} templates`);
    skipped.forEach(s => console.log(`  ❌ EMERGENCY SKIP: "${s.title}" - ${s.reason}`));
    
    return kept;
  }

  static calculateCompressedDueDate(template, timelinePressure, baseDate = new Date()) {
    const baseOffset = template.default_due_offset || 30; // Now signed

    let compressedOffset;
    const absBaseOffset = Math.abs(baseOffset); // Work with absolute value for compression

    switch (timelinePressure) {
      case 'crisis':
        compressedOffset = Math.max(Math.floor(absBaseOffset * 0.3), 14);
        break;
      case 'intensive':
        compressedOffset = Math.max(Math.floor(absBaseOffset * 0.5), 21);
        break;
      case 'steady':
        compressedOffset = Math.max(Math.floor(absBaseOffset * 0.75), 28);
        break;
      case 'comprehensive':
        compressedOffset = absBaseOffset;
        break;
      default:
        compressedOffset = absBaseOffset;
    }

    // Apply the original sign to compressed offset
    const finalOffset = baseOffset >= 0 ? compressedOffset : -compressedOffset;

    const dueDate = new Date(baseDate);
    dueDate.setDate(dueDate.getDate() + finalOffset);
    return dueDate.toISOString().split('T')[0];
  }

  /**
   * Get student timeline preferences for academic calendar awareness
   */
  static async getStudentTimelinePreferences(studentId) {
    try {
      const { data, error } = await supabase
        .from('student_timeline_preferences')
        .select('*')
        .eq('user_id', studentId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('[AdminMilestoneService] Error loading preferences:', error);
        return null;
      }

      return data || null;
    } catch (error) {
      console.error('[AdminMilestoneService] Error getting preferences:', error);
      return null;
    }
  }

  /**
   * Apply Early Decision Priority to college timeline generation
   */
  static async applyEarlyDecisionPriority(studentId, colleges) {
    try {
      const preferences = await this.getStudentTimelinePreferences(studentId);
      
      if (!preferences?.early_decision_priority) {
        return colleges;
      }

      return AcademicCalendarService.applyEarlyDecisionPriority(colleges, preferences);
    } catch (error) {
      console.error('[AdminMilestoneService] Error applying ED priority:', error);
      return colleges;
    }
  }

  /**
   * Generate academic calendar recommendations for student
   */
  static async generateAcademicRecommendations(studentId) {
    try {
      const preferences = await this.getStudentTimelinePreferences(studentId);
      
      if (!preferences) {
        return [];
      }

      // Get student's colleges
      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('user_id', studentId);

      if (!colleges) {
        return [];
      }

      return AcademicCalendarService.generateTimelineRecommendations(
        colleges, 
        preferences, 
        new Date()
      );
    } catch (error) {
      console.error('[AdminMilestoneService] Error generating recommendations:', error);
      return [];
    }
  }

  /**
   * NEW: Calculate due date using anchor system
   * Replaces calculateDueDateWithDeadlines with anchor-based approach
   */
  static async calculateDueDateWithAnchorSystem(template, student, college) {
    try {
      console.log(`\n🚨 [END-TO-END PIPELINE DEBUG] 🚨`);
      console.log(`[AdminMilestoneService] ===== MILESTONE GENERATION PIPELINE =====`);
      console.log(`[AdminMilestoneService] Template: "${template.title}" (ID: ${template.id})`);
      console.log(`[AdminMilestoneService] Student: ${student.first_name || 'Unknown'} ${student.last_name || 'Student'} (ID: ${student.id})`);
      console.log(`[AdminMilestoneService] Student baseline: ${student.timeline_baseline_date || 'null'}`);
      console.log(`[AdminMilestoneService] Student graduation year: ${student.graduation_year || 'null'}`);
      console.log(`[AdminMilestoneService] College: ${college?.name || 'N/A'}`);
      console.log(`[AdminMilestoneService] College application round: ${college?.application_round || 'null'}`);
      console.log(`[AdminMilestoneService] 🚀 Calling TaskDateCalculationService.calculateDueDate()...`);
      
      // Log template details for debugging
      console.log(`[AdminMilestoneService] 🔍 TEMPLATE DETAILS:`);
      console.log(`[AdminMilestoneService]   Anchor: ${template.anchor_id || 'null'}`);
      console.log(`[AdminMilestoneService]   Offset bounds:`, {
        max_rd_offset_days: template.max_rd_offset_days,
        min_rd_offset_days: template.min_rd_offset_days,
        max_ed_offset_days: template.max_ed_offset_days,
        min_ed_offset_days: template.min_ed_offset_days
      });
      console.log(`[AdminMilestoneService]   Legacy offset: ${template.offset_days || 'null'}`);
      
      const dueDate = await TaskDateCalculationService.calculateDueDate(
        template,
        student,
        college
      );
      
      // Return ISO date string for consistency with existing code
      const dateString = dueDate.toISOString().split('T')[0];
      
      // Critical validation for August 26 bug
      const studentStartDate = new Date(student.timeline_baseline_date || student.created_at);
      const isBeforeStart = dueDate < studentStartDate;
      
      console.log(`[AdminMilestoneService] 🎯 FINAL PIPELINE RESULT:`);
      console.log(`[AdminMilestoneService]   Calculated date: ${dateString}`);
      console.log(`[AdminMilestoneService]   Student start: ${studentStartDate.toISOString().split('T')[0]}`);
      console.log(`[AdminMilestoneService]   🚨 DATE VALIDATION: ${isBeforeStart ? '❌ BEFORE START DATE - BUG!' : '✅ After start date'}`);
      
      if (isBeforeStart) {
        console.error(`[AdminMilestoneService] 🔥🔥🔥 CRITICAL BUG DETECTED 🔥🔥🔥`);
        console.error(`[AdminMilestoneService] Task "${template.title}" scheduled for ${dateString}`);
        console.error(`[AdminMilestoneService] But student didn't start planning until ${studentStartDate.toISOString().split('T')[0]}`);
        console.error(`[AdminMilestoneService] This violates timeline logic!`);
      }
      
      console.log(`[AdminMilestoneService] ===== END MILESTONE GENERATION PIPELINE =====\n`);
      
      return dateString;
    } catch (error) {
      console.error(`[AdminMilestoneService] Error in anchor system calculation:`, error);

      // Fallback to simple calculation
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() + (template.minimum_lead_days || 7));
      return fallbackDate.toISOString().split('T')[0];
    }
  }

  /**
   * Push specific milestone templates to selected students
   * @param {Array} studentIds - Array of student IDs to push templates to
   * @param {Array} templateIds - Array of template IDs to push
   * @param {Object} options - Additional options for generation
   * @returns {Object} Result object with success status and details
   */
  static async pushSpecificTemplates(studentIds, templateIds, options = {}) {
    try {
      await ensureAuthentication();
      console.log(`[AdminMilestoneService] Pushing ${templateIds.length} templates to ${studentIds.length} students`);

      const results = {
        success: true,
        totalGenerated: 0,
        studentsProcessed: 0,
        errors: [],
        details: []
      };

      // Load the templates
      const { data: templates, error: templateError } = await supabase
        .from('milestone_templates')
        .select('*')
        .in('id', templateIds);

      if (templateError) {
        console.error('[AdminMilestoneService] Error loading templates:', templateError);
        return { success: false, error: templateError.message };
      }

      if (!templates || templates.length === 0) {
        return { success: false, error: 'No templates found' };
      }

      console.log(`[AdminMilestoneService] Loaded ${templates.length} templates for pushing`);

      // Process each student
      for (const studentId of studentIds) {
        try {
          // Get student profile for timeline calculations
          const { data: profile } = await supabase
            .from('student_academic_profiles')
            .select('*')
            .eq('user_id', studentId)
            .single();

          // Get student's colleges for college-specific templates
          const { data: colleges } = await supabase
            .from('college_journey_colleges')
            .select('*')
            .eq('user_id', studentId);

          const studentTasks = [];
          const timelinePressure = await this.calculateTimelinePressure(studentId);

          // Build proper student object for date calculations (matching pattern from generateMilestonesForStudent)
          const student = {
            id: studentId,
            user_id: studentId,
            timeline_pressure: timelinePressure,
            timeline_baseline_date: profile?.timeline_baseline_date,
            created_at: profile?.created_at,
            first_name: profile?.name?.split(' ')[0] || 'Student',
            last_name: profile?.name?.split(' ')[1] || '',
            graduation_year: profile?.graduation_year,
            name: profile?.name || 'Unknown Student'
          };

          for (const template of templates) {
            // Skip if template is college-specific but student has no colleges
            if (template.is_college_specific && (!colleges || colleges.length === 0)) {
              console.log(`[AdminMilestoneService] Skipping college-specific template ${template.id} - no colleges for student ${studentId}`);
              continue;
            }

            // Check for duplicates
            const { data: existingTasks } = await supabase
              .from('timeline_items')
              .select('id')
              .eq('user_id', studentId)
              .eq('source_type', 'milestone_template')
              .eq('source_id', template.id)
              .limit(1);

            if (existingTasks && existingTasks.length > 0 && !options.forceGenerate) {
              console.log(`[AdminMilestoneService] Skipping duplicate template ${template.id} for student ${studentId}`);
              continue;
            }

            if (template.is_college_specific) {
              // Generate for each college
              for (const college of colleges) {
                const dueDate = await this.calculateDueDateWithAnchorSystem(template, student, college);

                const task = {
                  user_id: studentId,
                  college_id: college.id,
                  title: template.title,
                  description: template.description || '',
                  category: template.category,
                  epic_id: template.epic_id || null,
                  priority: this.mapPriorityToTimelineItems(template.priority),
                  due_date: dueDate,
                  scheduled_date: dueDate,
                  workflow_phase: 'ready',
                  status: 'planned',
                  completion_rule: template.completion_rule,
                  source_type: 'milestone_template',
                  source_id: `template_${template.id}`,
                  estimated_duration: template.estimated_duration || 60,
                  module_name: template.module_name,
                  module_link: template.module_link || template.action_route,
                  resource_urls: template.resource_urls || [],
                  task_tip: template.task_tip,
                  // Crisis support fields (explicit propagation)
                  mvv_risk: template.mvv_risk,
                  incompletion_cost: template.incompletion_cost,
                  mvv_time_required: template.mvv_time_required,
                  optimal_time_required: template.optimal_time_required,
                  min_viable_version: template.min_viable_version,
                  optimal_version: template.optimal_version,
                  can_autogenerate: template.can_autogenerate,
                  autogen_quality: template.autogen_quality,
                  metadata: {
                    category: template.category,
                    phase: template.phase,
                    tier_level: template.tier_level,
                    priority: template.priority,
                    importance_level: template.importance_level,
                    cognitive_intensity: template.cognitive_intensity,
                    timing_sensitivity: template.timing_sensitivity,
                    generated_by: 'admin_push_templates',
                    pushed_at: new Date().toISOString(),
                    timeline_pressure: timelinePressure,
                    college_specific: true,
                    college_name: college.name
                  }
                };

                studentTasks.push(task);
              }
            } else {
              // Global template - generate once
              const dueDate = await this.calculateDueDateWithAnchorSystem(template, student, null);

              const task = {
                user_id: studentId,
                title: template.title,
                description: template.description || '',
                category: template.category,
                epic_id: template.epic_id || null,
                priority: this.mapPriorityToTimelineItems(template.priority),
                due_date: dueDate,
                scheduled_date: dueDate,
                workflow_phase: 'ready',
                status: 'planned',
                completion_rule: template.completion_rule,
                source_type: 'milestone_template',
                source_id: `template_${template.id}`,
                estimated_duration: template.estimated_duration || 60,
                module_name: template.module_name,
                module_link: template.module_link || template.action_route,
                resource_urls: template.resource_urls || [],
                task_tip: template.task_tip,
                // Crisis support fields (explicit propagation)
                mvv_risk: template.mvv_risk,
                incompletion_cost: template.incompletion_cost,
                mvv_time_required: template.mvv_time_required,
                optimal_time_required: template.optimal_time_required,
                min_viable_version: template.min_viable_version,
                optimal_version: template.optimal_version,
                can_autogenerate: template.can_autogenerate,
                autogen_quality: template.autogen_quality,
                metadata: {
                  category: template.category,
                  phase: template.phase,
                  tier_level: template.tier_level,
                  priority: template.priority,
                  importance_level: template.importance_level,
                  cognitive_intensity: template.cognitive_intensity,
                  timing_sensitivity: template.timing_sensitivity,
                  generated_by: 'admin_push_templates',
                  pushed_at: new Date().toISOString(),
                  timeline_pressure: timelinePressure,
                  college_specific: false
                }
              };

              studentTasks.push(task);
            }
          }

          // Insert tasks for this student
          if (studentTasks.length > 0) {
            const { error: insertError } = await supabase
              .from('timeline_items')
              .insert(studentTasks);

            if (insertError) {
              console.error(`[AdminMilestoneService] Error inserting tasks for student ${studentId}:`, insertError);
              results.errors.push({ studentId, error: insertError.message });
            } else {
              results.totalGenerated += studentTasks.length;
              results.studentsProcessed++;
              results.details.push({
                studentId,
                tasksCreated: studentTasks.length
              });
              console.log(`[AdminMilestoneService] Successfully pushed ${studentTasks.length} tasks to student ${studentId}`);

              // Log crisis field propagation for verification
              const tasksWithCrisisFields = studentTasks.filter(t =>
                t.incompletion_cost !== undefined || t.mvv_risk !== undefined
              );
              if (tasksWithCrisisFields.length > 0) {
                console.log(`[AdminMilestoneService] Crisis fields propagated to ${tasksWithCrisisFields.length} tasks:`,
                  tasksWithCrisisFields.map(t => ({
                    title: t.title,
                    incompletion_cost: t.incompletion_cost,
                    mvv_risk: t.mvv_risk,
                    has_time_estimates: !!(t.mvv_time_required && t.optimal_time_required)
                  }))
                );
              }
            }
          }
        } catch (studentError) {
          console.error(`[AdminMilestoneService] Error processing student ${studentId}:`, studentError);
          results.errors.push({ studentId, error: studentError.message });
        }
      }

      console.log(`[AdminMilestoneService] Push complete: ${results.totalGenerated} tasks to ${results.studentsProcessed} students`);
      return results;
    } catch (error) {
      console.error('[AdminMilestoneService] Error in pushSpecificTemplates:', error);
      return { success: false, error: error.message };
    }
  }
}

export default AdminMilestoneService;